/**
 * The trust boundary for the authentication loop.
 *
 * These schemas are the single source of truth: the client parses with them
 * before it sends, and the worker parses with them before it does anything at
 * all. Types come from `z.infer` — nothing here is written out twice.
 *
 * Every message is a MessageKey, never prose, because these errors are shown
 * to a Bulgarian-reading user.
 */

import { z } from 'zod'
import type { MessageKey } from './message-keys.ts'

/** Better Auth is configured with the same two numbers. Keep them together. */
export const PASSWORD_MIN_LENGTH = 12
export const PASSWORD_MAX_LENGTH = 256

/** RFC 5321 caps an address at 254 octets. */
const EMAIL_MAX_LENGTH = 254

const key = (k: MessageKey): string => k

export const emailSchema = z
  .string({ error: key('error.invalid_email') })
  .trim()
  .toLowerCase()
  .max(EMAIL_MAX_LENGTH, { error: key('error.invalid_email') })
  .pipe(z.email({ error: key('error.invalid_email') }))

export const passwordSchema = z
  .string({ error: key('error.password_required') })
  .min(PASSWORD_MIN_LENGTH, { error: key('error.password_too_short') })
  .max(PASSWORD_MAX_LENGTH, { error: key('error.password_too_long') })

/**
 * A Turnstile response token. Cloudflare documents these as at most 2048
 * characters; anything longer is not worth a round trip to siteverify.
 */
export const turnstileTokenSchema = z
  .string({ error: key('error.turnstile_missing') })
  .min(1, { error: key('error.turnstile_missing') })
  .max(2048, { error: key('error.turnstile_failed') })

/** A nickname's length, in characters. */
export const NICKNAME_MAX_LENGTH = 40

/**
 * The optional nickname (Better Auth's `user.name`): shown to the reader in
 * the header, and beside any review they write, so it is public from the
 * first review. Empty means none. No "@", so an email address cannot become
 * a public name by accident; no control characters.
 */
export const nicknameSchema = z
  .string({ error: key('error.nickname_invalid') })
  .trim()
  .max(NICKNAME_MAX_LENGTH, { error: key('error.nickname_invalid') })
  .refine((name) => !name.includes('@'), { error: key('error.nickname_invalid') })
  .refine((name) => ![...name].some((char) => char.charCodeAt(0) < 0x20 || char.charCodeAt(0) === 0x7f), {
    error: key('error.nickname_invalid'),
  })

export const registerInputSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  turnstileToken: turnstileTokenSchema,
  // Optional in the true sense: absent is the same as empty.
  nickname: nicknameSchema.default(''),
})

export const signInInputSchema = z.object({
  email: emailSchema,
  password: z.string({ error: key('error.password_required') }).min(1, {
    error: key('error.password_required'),
  }),
})

export const requestPasswordResetInputSchema = z.object({
  email: emailSchema,
  turnstileToken: turnstileTokenSchema,
})

export const resetPasswordInputSchema = z.object({
  token: z.string({ error: key('error.token_invalid') }).min(1, {
    error: key('error.token_invalid'),
  }),
  password: passwordSchema,
})

/**
 * The resend form on the failed-verification page. It was the one form in the
 * loop that submitted without parsing (issue #33): the server was never at
 * risk, since Better Auth validates and the route is throttled per address,
 * but a mistyped address made a round trip instead of an inline message.
 */
export const resendVerificationInputSchema = z.object({
  email: emailSchema,
})

/**
 * Deleting an account is irreversible, so it takes the password again. The
 * typed confirmation word is checked on the client for friction; the password
 * is what the server actually trusts.
 */
export const deleteAccountInputSchema = z.object({
  password: z.string({ error: key('error.password_required') }).min(1, {
    error: key('error.password_required'),
  }),
})

// A `z.infer` alias per schema stood here and none was imported. The forms all
// use `parsed.data` from a `safeParse`, where the type is inferred at the call
// site and never named. `project-conventions` says to derive types with
// `z.infer` rather than writing them twice — that rule stands, and this is what
// it looks like when there is nothing to derive them *for* yet. Add the one
// alias you need at the moment a function signature needs to name its input.
